import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getAppliance, addServiceRecord, deleteServiceRecord } from '../utils/api';
import { useApp } from '../context/AppContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import DocumentsSection from '../components/documents/DocumentsSection';

const fmt = (date) => date ? new Date(date).toLocaleDateString() : '—';

const blankService = { date: '', description: '', cost: '', technician: '', notes: '' };

const ApplianceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { removeAppliance } = useApp();
  const { confirm } = useConfirm();
  const { showToast } = useToast();
  const [appliance, setAppliance] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [serviceForm, setServiceForm] = useState(blankService);
  const [saving, setSaving] = useState(false);

  const load = () =>
    getAppliance(id)
      .then(({ data }) => setAppliance(data))
      .catch(() => setLoadError('Appliance not found.'));

  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const handleDelete = async () => {
    if (!(await confirm({ title: 'Delete appliance', message: `"${appliance.name}" and its service history will be permanently removed.` }))) return;
    try {
      await removeAppliance(id);
      navigate('/appliances');
    } catch { /* toast shown by removeAppliance */ }
  };

  const handleServiceChange = (e) => {
    const { name, value } = e.target;
    setServiceForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddService = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await addServiceRecord(id, serviceForm);
      setServiceForm(blankService);
      setShowServiceForm(false);
      await load();
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!(await confirm({ title: 'Remove service record', message: 'This service record will be permanently removed.' }))) return;
    try {
      await deleteServiceRecord(id, serviceId);
      await load();
      showToast('Service record removed', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to remove record', 'error');
    }
  };

  if (loadError) return <div className="page"><p className="empty-state">{loadError}</p></div>;
  if (!appliance) return <div className="loading">Loading…</div>;

  const sorted = [...appliance.serviceHistory].sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>{appliance.name}</h1>
          <p className="page-subtitle">{appliance.category.replace('-', ' ')}{appliance.room ? ` · ${appliance.room.name}` : ''}</p>
        </div>
        <div className="header-actions">
          <Link to={`/appliances/${id}/edit`} className="btn btn-secondary">Edit</Link>
          <button className="btn btn-danger" onClick={handleDelete}>Delete</button>
        </div>
      </div>

      <div className="detail-grid">
        <section className="detail-section">
          <h2>Details</h2>
          <dl className="detail-list">
            <dt>Make</dt><dd>{appliance.make || '—'}</dd>
            <dt>Model</dt><dd>{appliance.model || '—'}</dd>
            <dt>Serial #</dt><dd>{appliance.serialNumber || '—'}</dd>
            <dt>Purchased</dt><dd>{fmt(appliance.purchaseDate)}</dd>
            <dt>Installed</dt><dd>{fmt(appliance.installDate)}</dd>
            <dt>Warranty Expiry</dt><dd>{fmt(appliance.warrantyExpiry)}</dd>
          </dl>
          {appliance.notes && (
            <div className="detail-notes">
              <h3>Notes</h3>
              <p>{appliance.notes}</p>
            </div>
          )}
        </section>

        <section className="detail-section">
          <div className="section-header">
            <h2>Service History</h2>
            <button className="btn btn-primary btn-sm" onClick={() => setShowServiceForm((v) => !v)}>
              {showServiceForm ? 'Cancel' : '+ Add Record'}
            </button>
          </div>

          {showServiceForm && (
            <form onSubmit={handleAddService} className="service-form">
              <div className="form-row">
                <label>Date *
                  <input type="date" name="date" value={serviceForm.date} onChange={handleServiceChange} required />
                </label>
                <label>Technician
                  <input name="technician" value={serviceForm.technician} onChange={handleServiceChange} placeholder="Name or company" />
                </label>
                <label>Cost ($)
                  <input type="number" name="cost" value={serviceForm.cost} onChange={handleServiceChange} min={0} step="0.01" />
                </label>
              </div>
              <label>Description *
                <input name="description" value={serviceForm.description} onChange={handleServiceChange} required placeholder="What was done?" />
              </label>
              <label>Notes
                <textarea name="notes" value={serviceForm.notes} onChange={handleServiceChange} rows={2} />
              </label>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving…' : 'Add Record'}
              </button>
            </form>
          )}

          {sorted.length === 0 ? (
            <p className="empty-state">No service records yet.</p>
          ) : (
            <ul className="service-list">
              {sorted.map((s) => (
                <li key={s._id} className="service-item">
                  <div className="service-item-header">
                    <span className="service-date">{fmt(s.date)}</span>
                    {s.cost != null && s.cost !== '' && (
                      <span className="service-cost">${Number(s.cost).toFixed(2)}</span>
                    )}
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDeleteService(s._id)}
                    >
                      Remove
                    </button>
                  </div>
                  <p className="service-description">{s.description}</p>
                  {s.technician && <p className="service-meta">By: {s.technician}</p>}
                  {s.notes && <p className="service-meta">{s.notes}</p>}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <div style={{ marginTop: '1.5rem' }}>
        <DocumentsSection applianceId={id} />
      </div>
    </div>
  );
};

export default ApplianceDetail;
